// src/hooks/useLogo.js
import { useState, useEffect } from "react";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "../firebaseClient";
import { useAppSettings } from "./useAppSettings";
import { logError } from "../lib/logger";

/**
 * useLogo
 *
 * Fetches the Meds Cafe logo URL from either:
 * 1. Settings (medscafeLogoUrl) - if set, uses that URL directly
 * 2. Firebase Storage - if no URL in settings, tries to fetch from storage at 'logos/medscafe-logo.png'
 *
 * Returns: { logoUrl, loading, error }
 */
export function useLogo() {
  const { settings, loading: settingsLoading } = useAppSettings();
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchLogo() {
      if (settingsLoading) {
        return; // Wait for settings to load
      }

      setLoading(true);
      setError(null);

      try {
        // First, check if logo URL is set in settings
        if (settings?.medscafeLogoUrl) {
          const configuredUrl = settings.medscafeLogoUrl;
          if (/^gs:\/\//i.test(configuredUrl)) {
            const storageRef = ref(storage, configuredUrl);
            const resolvedUrl = await getDownloadURL(storageRef);
            setLogoUrl(resolvedUrl);
            setLoading(false);
            return;
          }

          if (/^https?:\/\//i.test(configuredUrl)) {
            setLogoUrl(configuredUrl);
            setLoading(false);
            return;
          }
        }

        // If no URL in settings, try to fetch from Firebase Storage
        try {
          // Try the actual uploaded filenames first
          const logoRef = ref(storage, "logos/meds-cafe-logo.png");
          const url = await getDownloadURL(logoRef);
          setLogoUrl(url);
        } catch (storageError) {
          // If logo doesn't exist in storage, try alternative paths
          const alternativePaths = [
            "logos/meds-cafe-logo.webp", // WebP version
            "logos/medscafe-logo.png", // Original naming convention
            "logos/logo.png",
            "logos/medscafe.png",
            "branding/logo.png",
            "branding/medscafe-logo.png",
            "branding/meds-cafe-logo.png",
          ];

          let found = false;
          for (const path of alternativePaths) {
            try {
              const altRef = ref(storage, path);
              const url = await getDownloadURL(altRef);
              setLogoUrl(url);
              found = true;
              break;
            } catch (e) {
              // Continue to next path
            }
          }

          if (!found) {
            // No logo found, but that's okay - we'll use a fallback
            setLogoUrl(null);
          }
        }
      } catch (err) {
        logError("Error fetching logo", err);
        setError(err);
        setLogoUrl(null);
      } finally {
        setLoading(false);
      }
    }

    fetchLogo();
  }, [settings, settingsLoading]);

  return { logoUrl, loading, error };
}
